import { DISCOVERY_DURATION_MS } from "@/api/mock-data/agent-estate/phases";
import {
  BUSINESS_UNIT_LABELS,
  getMcpServerCatalog,
  getMcpToolCount,
  McpServerDefinition,
  PLATFORM_LABELS,
} from "@/api/mock-data/agent-estate/seed";
import { agentEstateStore } from "@/api/mock-data/agent-estate/store";
import {
  snapshotPlatforms,
  snapshotGovernanceOverview,
  snapshotEstateAssets,
} from "@/api/mock-data/investment-banking/governance";
import { demoRelationships } from "@/api/mock-data/investment-banking/relationships";
import { snapshotAccounts } from "@/api/mock-data/investment-banking/snapshot";
import type { AgentScope } from "@/api/temp/AgentEstateService";
import {
  Agent,
  AgentAccessFlow,
  AgentAccessFlowListParams,
  AgentFlowSummary,
  AuditEvidenceRecord,
  ControlCategory,
  ControlDecision,
  DataGovernanceOverview,
  DataPlatformGovernance,
  EnforcementResult,
  EnterprisePosture,
  EstateAsset,
  EstateInventory,
  EstateMetricId,
  EstateReadiness,
  GuardianContextKind,
  GuardianPromptSuggestion,
  GuardianRecommendation,
  GuardianScriptedReply,
  IncidentAdvisory,
  OutcomeDelta,
  PolicyPosture,
  ProofPanel,
  RelationshipGraph,
  VerifiedOutcomes,
  WhatsChanged,
} from "@/api/temp/TrustAIExecutiveService";
import {
  agentSessionScope,
  readAgentSession,
  registerAgentSessionReset,
  writeAgentSession,
} from "@/libs/agentSession";
import { isInvestmentBankingDemo } from "@/libs/investmentBankingDemo";
import { getDashboardLifecycleState } from "@/pages/TrustAIExecutiveBoard/lifecycle";
import {
  buildDataGovernanceOverview,
  dataPlatformGovernance,
} from "./dataGovernance";
import { buildRelationshipGraph } from "./estate";
import { guardianPromptsFor, guardianReplyFor } from "./guardian";
import {
  asLifecycleScenario,
  EVALUATION_DURATION_MS,
  LifecycleScenario,
  readinessFor,
} from "./lifecycle";
import {
  buildAuditEvidence,
  controlDecisions,
  enterprisePosture,
  estateAssets,
  GLOBAL_PROTECTION_CONTROL_ID,
  guardianRecommendations,
  incidentAdvisory,
  priorityControls,
  VERIFIED_OUTCOMES_TARGET,
  whatsChanged,
} from "./seed";
import {
  type ExecutiveBoardSnapshot,
  isExecutiveBoardSnapshot,
} from "./session";

const PENDING_PROOF_PANEL: ProofPanel = {
  policy: "No policy enforced yet",
  scope: "—",
  enforcement: "Not yet active",
  outcome: "Awaiting approval",
  evidence: "No evidence generated yet",
  verificationStatus: "Pending",
};

/** Same counters as the target, zeroed: nothing is verified until enforcement. */
const ZERO_VERIFIED_OUTCOMES = Object.fromEntries(
  Object.keys(VERIFIED_OUTCOMES_TARGET).map(key => [key, 0]),
) as typeof VERIFIED_OUTCOMES_TARGET;

/**
 * Per-entity mock store for the TrustAI Executive Decision Board demo.
 *
 * There is no real backend for this feature - this store simulates the one
 * effect the demo needs: approving an enforcement action flips the board from
 * "recommended" to "verified" and produces audit evidence for whichever
 * control was enforced. Session storage preserves the run across refreshes;
 * authentication boundaries clear both memory and storage.
 */
export class TrustAIExecutiveStore {
  private loadedScope?: string;
  private readonly posture: EnterprisePosture =
    structuredClone(enterprisePosture);
  private readonly controls: ControlCategory[] =
    structuredClone(priorityControls);
  private readonly decisions: Record<string, ControlDecision> =
    structuredClone(controlDecisions);
  private readonly advisory: IncidentAdvisory =
    structuredClone(incidentAdvisory);
  private assets: EstateAsset[] = structuredClone(estateAssets);
  private lastAnalyzedAt = enterprisePosture.lastAnalyzedAt;
  private lastEnforced: ExecutiveBoardSnapshot["lastEnforced"] = null;

  /**
   * Which lifecycle scenario this session is in.
   *
   * Defaults to ZERO_DAY so a fresh dashboard load starts at day zero, before
   * anything has been connected.
   */
  private scenario: LifecycleScenario = "ZERO_DAY";
  private discoveryStartedAt: string | null = null;
  private evaluationStartedAt: string | null = null;
  private completedEvaluations = 0;
  /**
   * Whether this session's discovery run continues into evaluation by itself.
   *
   * Set only by `startOnboardingRun` — the onboarding wizard commits to one
   * continuous run, where connecting a platform from the catalog panel does
   * not and still parks at DISCOVERED for an explicit "Start evaluation".
   */
  private autoChain = false;

  private ensureSession(): void {
    const scope = agentSessionScope(isInvestmentBankingDemo());
    if (scope === this.loadedScope) return;
    this.clearMemory();
    this.loadedScope = scope;
    const saved = readAgentSession("board", scope, isExecutiveBoardSnapshot);
    if (!saved) return;
    this.assets = saved.assets;
    this.lastAnalyzedAt = saved.lastAnalyzedAt;
    this.lastEnforced = saved.lastEnforced;
    this.scenario = saved.scenario;
    this.discoveryStartedAt = saved.discoveryStartedAt;
    this.evaluationStartedAt = saved.evaluationStartedAt;
    this.completedEvaluations = saved.completedEvaluations;
    this.autoChain = saved.autoChain;
  }

  private persist(): void {
    if (this.loadedScope === undefined) return;
    writeAgentSession("board", this.loadedScope, {
      assets: this.assets,
      lastAnalyzedAt: this.lastAnalyzedAt,
      lastEnforced: this.lastEnforced,
      scenario: this.scenario,
      discoveryStartedAt: this.discoveryStartedAt,
      evaluationStartedAt: this.evaluationStartedAt,
      completedEvaluations: this.completedEvaluations,
      autoChain: this.autoChain,
    } satisfies ExecutiveBoardSnapshot);
  }

  clearSession(): void {
    this.loadedScope = undefined;
    this.clearMemory();
  }

  getPosture(): EnterprisePosture {
    this.ensureSession();
    return {
      ...this.posture,
      // Mirrors the real MCP server/tool catalog for this estate rather than
      // the enterprise-wide placeholder counts, exactly like "ai-agents"
      // below - a stat card and its own inventory drawer can't disagree.
      estate: this.posture.estate.map(metric => {
        // "data-sources" is deliberately not overridden here: the tenant's
        // own account list is the one authority for that count, and the board
        // reads that count from the account inventory instead (see
        // `useEstateExperience`).
        if (metric.id === "ai-agents") {
          return {
            ...metric,
            value: agentEstateStore.snapshot().length,
            discoveredDelta: undefined,
          };
        }
        if (metric.id === "mcp-servers") {
          return {
            ...metric,
            value: getMcpServerCatalog().length,
            discoveredDelta: undefined,
          };
        }
        if (metric.id === "tools") {
          return {
            ...metric,
            value: getMcpToolCount(),
            discoveredDelta: undefined,
          };
        }
        return metric;
      }),
      lastAnalyzedAt: this.lastAnalyzedAt,
      policyPosture: this.getPolicyPosture(),
      outcomeDelta: this.getOutcomeDelta(),
    };
  }

  private getPolicyPosture(): PolicyPosture {
    const agents = agentEstateStore.snapshot();
    const notEvaluated = agents.filter(
      agent => agent.securityStatus === "NOT_EVALUATED",
    ).length;
    return {
      secure: agents.filter(agent => agent.securityStatus === "SECURE").length,
      notSecure: agents.filter(agent => agent.securityStatus === "NOT_SECURE")
        .length,
      notEvaluated,
      notEvaluatedBreakdown: [
        { id: "requirePolicyReview", count: notEvaluated },
      ],
    };
  }

  private getOutcomeDelta(): OutcomeDelta | undefined {
    if (!this.lastEnforced) return undefined;
    return {
      controlId: this.lastEnforced.controlId,
      title: isInvestmentBankingDemo() ? "Decision recorded" : "Mock decision recorded",
      summary:
        isInvestmentBankingDemo()
          ? "The reviewed decision has been recorded. Apply the relevant agent policies to update coverage. Runtime verification pending."
          : "This simulation records the reviewed decision. Apply mapped agent policies to change coverage; runtime enforcement requires backend verification.",
      metrics: [
        {
          id: "agents-in-scope",
          label: "Agents in reviewed scope",
          before: "0",
          after: String(this.lastEnforced.agentCount),
        },
      ],
    };
  }

  private agentsForControl(controlId: string) {
    const units = new Set(
      this.decisions[controlId]?.executive.affectedAgents.map(
        agent => agent.businessUnit,
      ),
    );
    return agentEstateStore
      .snapshot()
      .filter(
        agent =>
          agent.securityStatus !== "SECURE" &&
          (controlId === GLOBAL_PROTECTION_CONTROL_ID ||
            !units.size ||
            units.has(BUSINESS_UNIT_LABELS[agent.businessUnit]) ||
            units.has(agent.businessUnit)),
      );
  }

  listControls(): ControlCategory[] {
    this.ensureSession();
    // Derived, not stored: isEnforced is computed from lastEnforced on every
    // read, so reset() clearing that one field is all it takes to clear the
    // badge too -- no separate flag to remember to reset, and the
    // structuredClone'd seed array is never mutated.
    return this.controls
      .map(control => {
        // One membership per control, read once: the count, the violations,
        // the business units and the flow's agent ids all describe the same
        // distinct agents.
        const agents = this.agentsForControl(control.id);
        const agentIds = agents.map(agent => agent.id);
        return {
          ...control,
          affectedAgentCount: agents.length,
          affectedAgentIds: agentIds,
          violationCount: agents.reduce(
            (sum, agent) =>
              sum +
              agent.recentActivity.filter(event => event.governanceViolation)
                .length,
            0,
          ),
          affectedBusinessUnits: [
            ...new Set(
              agents.map(agent => BUSINESS_UNIT_LABELS[agent.businessUnit]),
            ),
          ],
          ...(control.flowContext
            ? {
                flowContext: {
                  ...control.flowContext,
                  agentIds,
                },
              }
            : {}),
          isEnforced: this.lastEnforced?.controlId === control.id,
        };
      })
      .sort((a, b) => a.priorityRank - b.priorityRank);
  }

  getDecision(controlId: string): ControlDecision | undefined {
    this.ensureSession();
    const decision = this.decisions[controlId];
    if (!decision) return undefined;
    const agents = this.agentsForControl(controlId);
    const affectedAgents = agents.map(agent => ({
      id: agent.id,
      name: agent.name,
      businessUnit: BUSINESS_UNIT_LABELS[agent.businessUnit],
      application: agent.project,
      dataSource: agent.connectedResource,
      classification: "Sensitive",
      complianceFramework: decision.complianceImpact[0] ?? "NIST",
    }));
    const agentIds = agents.map(agent => agent.id);
    const first = agents[0];
    return {
      ...decision,
      affectedAgentIds: agentIds,
      affectedApplications: [...new Set(agents.map(agent => agent.project))],
      policyPack: {
        ...decision.policyPack,
        estimatedAgentCoverage: agents.length,
      },
      guardianEvidence: first
        ? {
            ...decision.guardianEvidence,
            agentId: first.id,
            agentName: first.name,
            platform: PLATFORM_LABELS[first.platform],
            affectedData: first.connectedResource,
            observedEvents: first.recentActivity.length,
            identityModel: "dedicated",
            sourceRegion: first.region,
            destinationRegion: first.region,
            missingPurpose: !first.purposeDescription,
            approvedBasis: first.policyGaps?.length ? "Missing" : "Approved",
            safeguardEvidence: first.policyGaps?.length ? "Missing" : "Present",
            controlGap: first.policyGaps?.map(gap => gap.title).join("; ") ?? "Evaluation pending",
            explanation: `${first.name} uses ${first.connectedResource} for ${first.purposeDescription}. ${first.policyGaps?.length ?? 0} required policy gaps remain. ${isInvestmentBankingDemo() ? "Policy coverage is recorded; runtime enforcement has not been verified." : "These mock records describe coverage; runtime enforcement has not been verified."}`,
          }
        : decision.guardianEvidence,
      executive: { ...decision.executive, affectedAgents },
      impact: {
        ...decision.impact,
        defaultScopeId: "canonical-affected",
        scopeOptions: [
          {
            id: "canonical-affected",
            label: `${agents.length} agents with gaps in this investigation`,
            agentIds,
            agentCount: agentIds.length,
            applicationCount: new Set(agents.map(agent => agent.project)).size,
            businessUnitCount: new Set(agents.map(agent => agent.businessUnit))
              .size,
          },
        ],
      },
    };
  }

  listAgentRegistry(): Agent[] {
    this.ensureSession();
    return agentEstateStore.snapshot().map(agent => ({
      id: agent.id,
      name: agent.name,
      platform: PLATFORM_LABELS[agent.platform],
      identityModel: "dedicated",
      dataAccess: [
        ...new Set(agent.mcpServices.flatMap(service => service.dataAccess)),
      ],
      sourceRegion: agent.region,
      destinationRegion: agent.region,
      riskState:
        agent.securityStatus === "SECURE" ? "Governed" : "Needs review",
      policyPostureState: agent.securityStatus,
      governingPolicy:
        agent.appliedPolicies.map(policy => policy.name).join(", ") ||
        undefined,
      policyGapReason:
        agent.policyGaps?.map(gap => gap.title).join("; ") || undefined,
      notEvaluatedReason:
        agent.securityStatus === "NOT_EVALUATED"
          ? "EVALUATION_PENDING"
          : undefined,
    }));
  }

  /** Business unit/owner label for a server's own agents, or a general
   * fallback for a server (like the Snowflake warehouse) not scoped to one. */
  private mcpServerUnitLabel(server: McpServerDefinition): string {
    return server.businessUnits.length
      ? BUSINESS_UNIT_LABELS[server.businessUnits[0]]
      : "Enterprise Data Platform";
  }

  private canonicalAssets(): EstateAsset[] {
    return [
      ...this.assets.filter(
        asset =>
          asset.type !== "ai-agents" &&
          asset.type !== "mcp-servers" &&
          asset.type !== "tools" &&
          (!isInvestmentBankingDemo() || asset.type !== "data-sources"),
      ),
      ...(isInvestmentBankingDemo() ? snapshotEstateAssets() : []),
      ...getMcpServerCatalog().map(server => ({
        id: server.id,
        name: server.name,
        type: "mcp-servers" as const,
        owner: this.mcpServerUnitLabel(server),
        businessUnit: this.mcpServerUnitLabel(server),
        environment: "Production" as const,
        status: server.tools.some(tool => tool.classification === "Destructive")
          ? ("Needs attention" as const)
          : ("Protected" as const),
        platform: server.technology,
        description: server.capabilities.join(", "),
        violationCount: 0,
        runtimeActive: true,
      })),
      ...getMcpServerCatalog().flatMap(server =>
        server.tools.map(tool => ({
          id: `${server.id}-${tool.name}`,
          name: tool.name,
          type: "tools" as const,
          owner: this.mcpServerUnitLabel(server),
          businessUnit: this.mcpServerUnitLabel(server),
          environment: "Production" as const,
          status:
            tool.classification === "Destructive"
              ? ("Needs attention" as const)
              : ("Protected" as const),
          platform: server.technology,
          description: tool.dataAccess,
          violationCount: 0,
          runtimeActive: true,
        })),
      ),
      ...agentEstateStore.snapshot().map(agent => ({
        id: agent.id,
        name: agent.name,
        type: "ai-agents" as const,
        owner: agent.owner,
        businessUnit: BUSINESS_UNIT_LABELS[agent.businessUnit],
        environment:
          agent.environmentType === "PRODUCTION"
            ? ("Production" as const)
            : agent.environmentType === "TEST"
              ? ("Staging" as const)
              : ("Development" as const),
        status:
          agent.securityStatus === "SECURE"
            ? ("Protected" as const)
            : ("Needs attention" as const),
        platform: PLATFORM_LABELS[agent.platform],
        description: agent.purposeDescription,
        violationCount: agent.recentActivity.filter(
          event => event.governanceViolation,
        ).length,
        runtimeActive: !agent.accessRevoked,
      })),
    ];
  }

  getEstateInventory(metricId: EstateMetricId): EstateInventory {
    this.ensureSession();
    const items = this.canonicalAssets().filter(
      asset => asset.type === metricId,
    );
    return { metricId, total: items.length, items };
  }

  getRelationshipGraph(): RelationshipGraph {
    this.ensureSession();
    const graph = buildRelationshipGraph(this.canonicalAssets());
    if (isInvestmentBankingDemo())
      return demoRelationships(graph, agentEstateStore.snapshot());
    return {
      ...graph,
      // Same override as `getPosture`: the graph is built from
      // `canonicalAssets()`, so its legend has to count the catalog it actually
      // rendered rather than the enterprise-wide placeholders in ESTATE_COUNTS.
      totals: {
        ...graph.totals,
        "ai-agents": agentEstateStore.snapshot().length,
        "mcp-servers": getMcpServerCatalog().length,
        tools: getMcpToolCount(),
      },
    };
  }

  getWhatsChanged(): WhatsChanged {
    this.ensureSession();
    return isInvestmentBankingDemo() ? { items: [] } : whatsChanged;
  }

  listGuardianRecommendations(): GuardianRecommendation[] {
    this.ensureSession();
    return guardianRecommendations.map(item => ({
      ...item,
      agentCount: this.agentsForControl(item.controlId).length,
    }));
  }

  /* ------------------------------------------------- estate readiness */

  /**
   * @param scenario - an explicit scenario to serve instead of this session's,
   *   for the mock-only `?dashboardState=` switcher. Read-only: an override
   *   never moves the store, so clearing the param returns the reader to
   *   whatever they had actually connected.
   */
  getEstateReadiness(scenario?: string): EstateReadiness {
    this.ensureSession();
    const override = asLifecycleScenario(scenario);
    return readinessFor(override ?? this.scenario, {
      discoveryStartedAt: this.discoveryStartedAt,
      evaluationStartedAt: this.evaluationStartedAt,
      completedEvaluations: this.completedEvaluations,
      // An explicit `?dashboardState=` park is read-only: it asks for a
      // deterministic fixture, and reshaping it with this session's chain
      // would make the switcher serve something other than what it names.
      autoChainEvaluation: override ? false : this.autoChain,
    });
  }

  startEvaluation(): EstateReadiness {
    this.ensureSession();
    if (this.scenario === "DISCOVERED") {
      this.scenario = "EVALUATING";
      this.evaluationStartedAt = new Date().toISOString();
      this.persist();
    }
    return this.getEstateReadiness();
  }

  /**
   * The onboarding wizard's Start Onboarding.
   *
   * Starts discovery and carries on into evaluation without a second click —
   * the administrator has already said what they want, and asking again
   * mid-run is the product not having listened.
   *
   * Deliberately does NOT reset the agent-estate store:
   * `startAgentOnboardingUsingPost` has just created the environment the
   * wizard collected, and `reset()` is `resetToDayZero()` plus a re-seed, which
   * would delete it and put the back-dated seed in its place.
   */
  startOnboardingRun(_platformId: string): EstateReadiness {
    this.ensureSession();
    if (this.scenario === "ZERO_DAY") {
      this.scenario = "DISCOVERING";
      this.discoveryStartedAt = new Date().toISOString();
    }
    this.autoChain = true;
    this.persist();
    return this.getEstateReadiness();
  }

  /**
   * Park the session in one scenario. The demo/test sibling of
   * `agentEstateStore.resetToDayZero()`, and it keeps the two stores in step
   * for the same reason `startOnboardingRun` does.
   */
  setScenario(scenario: LifecycleScenario): void {
    this.loadedScope = agentSessionScope(isInvestmentBankingDemo());
    this.scenario = scenario;
    // Deliberately no clock: parking a state should be deterministic, so the
    // fixture's own mid-run snapshot is served as-is. A stamp here would start
    // the run at 0%, where every count reads "not discovered yet" and the
    // state is indistinguishable from the one before it. Only a transition the
    // reader actually made (`startOnboardingRun`, `startEvaluation`) animates.
    this.discoveryStartedAt = null;
    this.evaluationStartedAt = null;
    // Parking a state is not a run, so nothing is chained out of it.
    this.autoChain = false;
    // A first evaluation has no baseline, so a session that is *about to*
    // evaluate has banked none. ACTIVE's fixture supplies its own.
    this.completedEvaluations = 0;

    if (scenario === "ZERO_DAY") agentEstateStore.resetToDayZero();
    else agentEstateStore.reset();
    this.persist();
  }

  enforce(
    controlId: string,
    scopeId: string,
    agentCount: number,
    agentIds: string[],
    includeFutureAgents = false,
  ): EnforcementResult {
    this.ensureSession();
    const decision = this.getDecision(controlId);
    // The caller's resolved count wins: business filters can narrow the scope
    // past any predefined ScopeOption, so the option's own agentCount is only
    // a fallback for calls that don't supply one.
    const enforcedAgentCount =
      agentCount ||
      decision?.impact.scopeOptions.find(o => o.id === scopeId)?.agentCount ||
      decision?.policyPack.estimatedAgentCoverage ||
      0;
    const resolvedAgentIds = agentIds.slice(0, enforcedAgentCount);
    this.lastEnforced = {
      controlId,
      scopeId,
      agentCount: enforcedAgentCount,
      agentIds: resolvedAgentIds,
      includeFutureAgents,
    };
    if (controlId === GLOBAL_PROTECTION_CONTROL_ID) {
      const protectedIds = new Set(resolvedAgentIds);
      this.assets = this.assets.map(asset =>
        protectedIds.has(asset.id)
          ? { ...asset, status: "Protected" as const, violationCount: 0 }
          : asset,
      );
    }
    this.persist();
    return {
      controlId,
      scopeId,
      enforcedAgentCount,
      blockedEventCount: isInvestmentBankingDemo()
        ? 0
        : (decision?.impact.recentRiskyActivitiesBlocked ?? 0),
      coversFutureAgents: includeFutureAgents,
    };
  }

  getVerifiedOutcomes(controlId: string): VerifiedOutcomes {
    this.ensureSession();
    if (isInvestmentBankingDemo())
      return {
        ...ZERO_VERIFIED_OUTCOMES,
        proofPanel: {
          ...PENDING_PROOF_PANEL,
          policy: this.lastEnforced
            ? "Policy applied"
            : PENDING_PROOF_PANEL.policy,
          outcome: "Runtime verification pending",
          evidence: "No runtime evidence is available",
          verificationStatus: "Pending",
        },
      };
    const verified = this.lastEnforced !== null;
    const decision = this.decisions[this.lastEnforced?.controlId ?? controlId];
    const enforcedAgentCount =
      this.lastEnforced?.agentCount ??
      decision?.policyPack.estimatedAgentCoverage ??
      0;

    return {
      ...ZERO_VERIFIED_OUTCOMES,
      proofPanel:
        verified && decision
          ? {
              policy: decision.policyPack.name,
              scope: `${enforcedAgentCount} agents`,
              enforcement: "Simulated authorization",
              outcome: "Mock decision recorded; runtime verification pending",
              evidence: "No new runtime evidence from policy application",
              verificationStatus: "Simulated",
            }
          : PENDING_PROOF_PANEL,
    };
  }

  listAuditEvidence(): AuditEvidenceRecord[] {
    this.ensureSession();
    if (isInvestmentBankingDemo() || !this.lastEnforced) return [];
    return buildAuditEvidence(this.lastEnforced.controlId);
  }

  listRelevantIntelligence(): IncidentAdvisory[] {
    this.ensureSession();
    return [this.advisory];
  }

  /* ----------------------------------------------------------- guardian */

  /**
   * Guardian is a pure read over the script seed — it holds no conversation
   * state, because the transcript belongs to the authenticated Guardian session
   * and should not survive a reload. Nothing to add to `reset()`.
   */
  getGuardianContext(params: {
    contextKey: string;
    contextKind: GuardianContextKind;
    label: string;
    metadata?: Record<string, unknown>;
  }): GuardianScriptedReply {
    this.ensureSession();
    const { contextKey, contextKind, label, metadata } = params;
    // `askGuardian` already redirects an empty estate to its setup script.
    const reply = this.askGuardian(
      contextKey,
      contextKind,
      label,
      null,
      "",
      metadata,
    );
    return { ...reply, contextKey };
  }

  listGuardianPrompts(
    contextKey: string,
    contextKind: GuardianContextKind,
    label: string,
  ): GuardianPromptSuggestion[] {
    this.ensureSession();
    if (
      [
        "estate",
        "business-function",
        "ai-platform",
        "data-platform",
        "agent",
        "risk",
        "recommendation",
      ].includes(contextKind)
    )
      return [
        {
          id: "context-attention",
          prompt: contextKind === "data-platform" ? `Which agents access ${label}?` : `Which agents in ${label} need attention?`,
          displayOrder: 0,
        },
        {
          id: "context-policy-gaps",
          prompt: `What policy gaps affect ${label}?`,
          displayOrder: 1,
        },
      ];
    return guardianPromptsFor(contextKey, contextKind, label);
  }

  askGuardian(
    contextKey: string,
    contextKind: GuardianContextKind,
    label: string,
    promptId: string | null,
    query: string,
    metadata?: Record<string, unknown>,
  ): GuardianScriptedReply {
    this.ensureSession();
    const lifecycleState = getDashboardLifecycleState(
      this.getEstateReadiness(),
    );
    if (contextKind === "estate" && lifecycleState !== "ACTIVE")
      return guardianReplyFor(
        `setup:${lifecycleState}`,
        "setup",
        promptId,
        query,
        label,
      );
    if (contextKind === "data-platform" && (contextKey === "data-estate" || contextKey.startsWith("data-platform:"))) {
      const value = contextKey.includes(":")
        ? contextKey.slice(contextKey.indexOf(":") + 1)
        : contextKey;
      // The caller's figures are the tenant's own data sources (the same list
      // the Data Sources tab counts). The seeded governance list only stands in
      // for the onboarding preview when the caller sent none.
      const supplied = Array.isArray(metadata?.dataPlatforms)
        ? (metadata.dataPlatforms as Array<{
            id: string;
            name: string;
            dataSourceTotal: number;
            incompleteCount: number;
            accessingAgentCount?: number;
          }>)
        : undefined;
      const platforms = (
        supplied ??
        (isInvestmentBankingDemo() ? this.listDataPlatformGovernance() : [])
      ).filter(
        platform =>
          supplied !== undefined ||
          contextKey === "data-estate" ||
          platform.id === value ||
          platform.name === value,
      );
      if (platforms.length)
        return {
          id: `${contextKey}:${promptId ?? "intro"}`,
          contextKey,
          suggestions: this.listGuardianPrompts(contextKey, contextKind, label),
          markdown: `**Context: ${label}**\n\n${platforms.map(platform => `- ${platform.name}: ${platform.dataSourceTotal} data sources, ${platform.incompleteCount} requiring attention${platform.accessingAgentCount === undefined ? "." : `; ${platform.accessingAgentCount} agents have access paths to this platform.`}`).join("\n")}\n\nInspect the relevant access paths and policy gaps before reviewing changes.`,
        };
    }
    const agents = agentEstateStore.snapshot();
    if (contextKey.startsWith("policies:") && metadata?.scope) {
      const environment = agentEstateStore.listEnvironments().at(-1);
      if (environment) {
        const preview = agentEstateStore.previewPolicies({
          environmentId: environment.id,
          scope: metadata.scope as AgentScope,
          policyIds: (metadata.selectedIds ?? []) as string[],
        });
        return {
          id: `${contextKey}:${promptId ?? "intro"}`,
          contextKey,
          suggestions: this.listGuardianPrompts(contextKey, contextKind, label),
          markdown: `**Context: ${label}**\n\n${preview.scopeAgentCount} agents in scope · will apply to ${preview.affectedAgentCount} · ${preview.newlySecureCount} of those expected to have their remaining policy gaps resolved.\n\n${preview.policies.map(policy => `- ${policy.policyName}: will apply to ${policy.agents.length} agents, ${policy.excludedAgentCount - policy.alreadyAppliedCount} outside its scope, ${policy.alreadyAppliedCount} already have it.`).join("\n")}\n\nReview the addressed gaps and exclusions before applying. ${isInvestmentBankingDemo() ? "Runtime verification pending." : "Mock assignments do not verify runtime enforcement."}`,
        };
      }
    }
    if (
      agents.length &&
      [
        "estate",
        "business-function",
        "ai-platform",
        "agent",
        "risk",
        "recommendation",
      ].includes(contextKind)
    ) {
      const [dimension, ...valueParts] = contextKey.split(":");
      const value = valueParts.join(":");
      const fields = {
        BUSINESS_UNIT: "businessUnit",
        PURPOSE: "purpose",
        SECURITY_STATUS: "securityStatus",
        PROJECT: "project",
        ENVIRONMENT: "environmentType",
        PLATFORM: "platform",
        CONNECTED_RESOURCE: "connectedResource",
      } as const;
      const field = fields[dimension as keyof typeof fields];
      let scoped = field
        ? agents.filter(agent => agent[field] === value)
        : agents;
      if (contextKind === "agent")
        scoped = agents.filter(agent => agent.id === value);
      if (contextKind === "risk" || contextKind === "recommendation") {
        const controlId =
          contextKind === "risk"
            ? value
            : (guardianRecommendations.find(item => item.id === value)
                ?.controlId ?? value);
        scoped = this.agentsForControl(controlId);
      }
      if (contextKey.startsWith("agents:")) {
        try {
          const filters = JSON.parse(contextKey.slice(7)) as Record<
            string,
            string
          >;
          scoped = agents.filter(agent =>
            Object.entries(filters).every(([key, expected]) => {
              if (!expected) return true;
              if (key === "q")
                return `${agent.name} ${agent.purposeDescription}`
                  .toLowerCase()
                  .includes(expected.toLowerCase());
              if (key === "hasRecommendations")
                return expected !== "true" || agent.recommendedPolicyCount > 0;
              return agent[key as keyof typeof agent] === expected;
            }),
          );
        } catch {
          scoped = [];
        }
      }
      const secure = scoped.filter(
        agent => agent.securityStatus === "SECURE",
      ).length;
      const notSecure = scoped.filter(
        agent => agent.securityStatus === "NOT_SECURE",
      ).length;
      const notEvaluated = scoped.length - secure - notSecure;
      const gaps = scoped.flatMap(agent => agent.policyGaps ?? []);
      const detail =
        promptId?.includes("policy") || query.toLowerCase().includes("gap")
          ? [...new Set(gaps.map(gap => gap.title))]
              .slice(0, 5)
              .map(title => `- ${title}`)
              .join("\n")
          : "Review agents with policy gaps, inspect Access Flow, then review the applicable policies before applying them. Agents awaiting evaluation remain Not Evaluated.";
      return {
        id: `${contextKey}:${promptId ?? "intro"}`,
        contextKey,
        suggestions: this.listGuardianPrompts(contextKey, contextKind, label),
        ...(contextKind === "risk" &&
        this.controls.find(control => control.id === value)?.flowContext
          ? { accessFlowFindingId: value }
          : {}),
        markdown: `**Context: ${label}**\n\n${scoped.length} agent${scoped.length === 1 ? "" : "s"} · ${secure} Secure · ${notSecure} Not Secure · ${notEvaluated} Not Evaluated\n\n${detail}`,
      };
    }
    const lifecycle = getDashboardLifecycleState(this.getEstateReadiness());
    const setup = contextKind === "estate" && lifecycle !== "ACTIVE";
    const reply = guardianReplyFor(
      setup ? `setup:${lifecycle}` : contextKey,
      setup ? "setup" : contextKind,
      promptId,
      query,
      label,
    );
    // Derived from the control's own relationship evidence rather than
    // scripted per answer: if a finding has no flow context, the action never
    // appears, whatever the script says.
    const controlId = contextKey.startsWith("risk:")
      ? contextKey.slice("risk:".length)
      : null;
    const control = controlId
      ? priorityControls.find(item => item.id === controlId)
      : undefined;
    return control?.flowContext
      ? { ...reply, accessFlowFindingId: control.id }
      : reply;
  }

  /* ------------------------------------------- governance landscape: data */

  getDataGovernanceOverview(): DataGovernanceOverview {
    this.ensureSession();
    return isInvestmentBankingDemo()
      ? snapshotGovernanceOverview()
      : buildDataGovernanceOverview();
  }

  listDataPlatformGovernance(): DataPlatformGovernance[] {
    this.ensureSession();
    // Platforms needing attention first, then by size - the same triage order
    // the AI categories use, so one reading habit works on both tabs.
    if (isInvestmentBankingDemo()) {
      const agents = agentEstateStore.snapshot();
      return snapshotPlatforms().map(platform => {
        const sourceIds = new Set(
          snapshotAccounts
            .filter(item => item.type === platform.id)
            .map(item => item.id),
        );
        const reaches = agents.filter(agent =>
          agent.mcpServices.some(server =>
            server.tools.some(tool => sourceIds.has(tool.dataSourceId)),
          ),
        );
        return {
          ...platform,
          accessingAgentCount: reaches.length,
          providingMcpServerCount: new Set(
            reaches.flatMap(agent =>
              agent.mcpServices
                .filter(server =>
                  server.tools.some(tool => sourceIds.has(tool.dataSourceId)),
                )
                .map(server => server.id),
            ),
          ).size,
        };
      });
    }
    return dataPlatformGovernance
      .map(platform => ({
        ...platform,
        accessingAgentCount: this.flows().filter(flow =>
          flow.branches.some(
            branch => branch.dataPlatform.detail === platform.name,
          ),
        ).length,
      }))
      .sort(
        (left, right) =>
          right.incompleteCount - left.incompleteCount ||
          right.dataSourceTotal - left.dataSourceTotal ||
          left.name.localeCompare(right.name),
      );
  }

  getDataPlatformGovernance(
    platformId: string,
  ): DataPlatformGovernance | undefined {
    this.ensureSession();
    return this.listDataPlatformGovernance().find(
      platform => platform.id === platformId,
    );
  }

  /* ------------------------------------------------------ access flows */

  private flows(): AgentAccessFlow[] {
    return agentEstateStore
      .snapshot()
      .flatMap(agent => (agent.accessFlow ? [agent.accessFlow] : []));
  }

  getAgentFlowSummary(): AgentFlowSummary {
    this.ensureSession();
    const flows = this.flows();
    const agents = agentEstateStore.snapshot();
    // Facets are derived from the flows themselves, so a platform added to the
    // seed appears in the filter without a second edit.
    return {
      agentsNeedingAttention: agents.filter(
        agent => agent.securityStatus === "NOT_SECURE",
      ).length,
      serviceAccountsToReview: agents.filter(
        agent => agent.securityStatus === "NOT_EVALUATED",
      ).length,
      // Distinct modelled paths: one per (agent, branch), however many times a
      // flow lists it - never an agent count or a sum of edges.
      pathsWithPolicyGaps: new Set(
        flows.flatMap(flow =>
          flow.branches
            .filter(branch => branch.accessState === "POLICY_GAP")
            .map(branch => `${flow.id}:${branch.id}`),
        ),
      ).size,
      businessUnits: [...new Set(flows.map(flow => flow.businessUnit))].sort(),
      dataPlatforms: [
        ...new Set(
          flows.flatMap(flow =>
            flow.branches.map(branch => branch.dataPlatform.detail),
          ),
        ),
      ].sort(),
    };
  }

  /**
   * Filtering happens here rather than in the page, so the flows view reads
   * like a real list endpoint and a backend can take it over unchanged.
   */
  listAgentAccessFlows(params: AgentAccessFlowListParams): AgentAccessFlow[] {
    this.ensureSession();
    const { businessUnit, dataPlatform, status = "attention" } = params;
    // Comma-joined rather than an array: `useQuery` keys on `values(params)`,
    // which has to stay primitive.
    const scopedIds = params.agentIds
      ? new Set(params.agentIds.split(",").filter(Boolean))
      : null;

    return this.flows().filter(flow => {
      if (scopedIds && !scopedIds.has(flow.id)) return false;
      if (businessUnit && flow.businessUnit !== businessUnit) return false;
      // Any branch matching is enough - an agent reaching Snowflake through
      // one MCP and SharePoint through another belongs in both platform views.
      if (
        dataPlatform &&
        !flow.branches.some(item => item.dataPlatform.detail === dataPlatform)
      ) {
        return false;
      }
      if (status === "attention") return flow.status === "NEEDS_ATTENTION";
      if (status === "secure") return flow.status === "SECURE";
      if (status === "not-evaluated") return flow.status === "NOT_EVALUATED";
      return true;
    });
  }

  getAgentAccessFlow(agentId: string): AgentAccessFlow | undefined {
    this.ensureSession();
    return this.flows().find(flow => flow.id === agentId);
  }

  reset(): void {
    this.loadedScope = agentSessionScope(isInvestmentBankingDemo());
    this.clearMemory();
    this.persist();
  }

  private clearMemory(): void {
    this.lastEnforced = null;
    this.lastAnalyzedAt = enterprisePosture.lastAnalyzedAt;
    this.assets = structuredClone(estateAssets);
    this.scenario = "ZERO_DAY";
    this.discoveryStartedAt = null;
    this.evaluationStartedAt = null;
    this.completedEvaluations = 0;
    this.autoChain = false;
  }

  /* ----------------------------------------------------------- test hooks */

  /**
   * Back-date a chained run to the moment discovery hands off to evaluation.
   *
   * One stamp drives the whole chain, so moving it is the only thing a spec has
   * to do to reach any point on the run — no fake timers, and the board's own
   * 2s poll picks the new state up on its next tick.
   */
  backdateOnboardingToEvaluation(): void {
    this.ensureSession();
    this.discoveryStartedAt = new Date(
      Date.now() - DISCOVERY_DURATION_MS - 1_000,
    ).toISOString();
    this.persist();
  }

  /** Back-date a chained run past the end of its evaluation half. */
  backdateOnboardingToComplete(): void {
    this.ensureSession();
    this.discoveryStartedAt = new Date(
      Date.now() - DISCOVERY_DURATION_MS - EVALUATION_DURATION_MS - 1_000,
    ).toISOString();
    this.persist();
  }
}

export const trustAiExecutiveStore = new TrustAIExecutiveStore();
registerAgentSessionReset("board", () => trustAiExecutiveStore.clearSession());
